import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <Header />
      <div className="legal-header">
        <div className="legal-header-content">
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 64, height: 64, background: '#C0492A', borderRadius: 18, marginBottom: 20, fontSize: 30 }}>🛡️</div>
          <h1>Privacy Policy</h1>
          <p style={{ fontSize: 18, color: '#B5B0A5', margin: '14px 0 6px' }}>Your privacy matters to us.</p>
          <p className="legal-date">Last updated: December 2025</p>
        </div>
      </div>

      <div className="legal-content">
        <section className="legal-section">
          <h2>1. Introduction</h2>
          <p>
            Custodian AI Automation ("we", "our", or "us") is committed to protecting your privacy 
            in accordance with the New Zealand Privacy Act 2020 and the Information Privacy Principles (IPPs). 
            This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information 
            when you use our AI voice automation services and website.
          </p>
          <p>
            By using our services, you consent to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Information We Collect</h2>
          <h3>2.1 Personal Information</h3>
          <p>We may collect the following personal information:</p>
          <ul>
            <li><strong>Contact Information:</strong> Name, email address, phone number, business name</li>
            <li><strong>Booking Information:</strong> Appointment dates, times, and any messages you provide</li>
            <li><strong>Communication Records:</strong> Records of your interactions with our AI voice systems</li>
            <li><strong>Technical Data:</strong> IP address, browser type, device information, and website usage data</li>
          </ul>

          <h3>2.2 How We Collect Information</h3>
          <p>We collect information:</p>
          <ul>
            <li>Directly from you when you book a demo, contact us, or use our services</li>
            <li>Automatically through cookies and similar technologies when you visit our website</li>
            <li>Through our AI voice systems during phone interactions</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>3. Purpose of Collection</h2>
          <p>Under IPP 1 of the Privacy Act 2020, we collect your personal information for the following purposes:</p>
          <ul>
            <li>To provide and improve our AI voice automation services</li>
            <li>To schedule and manage demo appointments</li>
            <li>To communicate with you about our services</li>
            <li>To respond to your enquiries and provide customer support</li>
            <li>To send you marketing communications (with your consent)</li>
            <li>To comply with legal obligations</li>
            <li>To protect our legitimate business interests</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Storage and Security</h2>
          <p>
            In accordance with IPP 5, we take reasonable steps to protect your personal information from:
          </p>
          <ul>
            <li>Unauthorised access, use, or disclosure</li>
            <li>Loss, misuse, or alteration</li>
          </ul>
          <p>
            Your data is stored on secure servers. We use industry-standard encryption and security 
            measures to protect your information. However, no method of transmission over the Internet 
            is 100% secure, and we cannot guarantee absolute security.
          </p>
          <p>
            We retain your personal information only for as long as necessary to fulfil the purposes 
            for which it was collected, or as required by law.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. Disclosure of Information</h2>
          <p>Under IPP 11, we may disclose your personal information to:</p>
          <ul>
            <li><strong>Service Providers:</strong> Third-party vendors who assist us in operating our business (e.g., cloud hosting, payment processors)</li>
            <li><strong>Legal Requirements:</strong> When required by law, court order, or government authority</li>
            <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            <li><strong>With Your Consent:</strong> For any other purpose with your explicit consent</li>
          </ul>
          <p>
            We do not sell your personal information to third parties.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Your Rights Under the Privacy Act 2020</h2>
          <p>You have the following rights regarding your personal information:</p>
          <ul>
            <li><strong>Right to Access (IPP 6):</strong> You can request access to the personal information we hold about you</li>
            <li><strong>Right to Correction (IPP 7):</strong> You can request correction of inaccurate or incomplete information</li>
            <li><strong>Right to Withdraw Consent:</strong> You can withdraw consent for marketing communications at any time</li>
            <li><strong>Right to Complain:</strong> You can lodge a complaint with the Office of the Privacy Commissioner</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the details provided below.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Cookies and Tracking Technologies</h2>
          <p>
            Our website uses cookies and similar tracking technologies to enhance your browsing experience. 
            Cookies are small text files stored on your device that help us:
          </p>
          <ul>
            <li>Remember your preferences</li>
            <li>Understand how you use our website</li>
            <li>Improve our services</li>
          </ul>
          <p>
            You can control cookies through your browser settings. However, disabling cookies may 
            affect the functionality of our website.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. AI Voice Recordings</h2>
          <p>
            When you interact with our AI voice systems, conversations may be recorded for:
          </p>
          <ul>
            <li>Quality assurance and training purposes</li>
            <li>Improving our AI services</li>
            <li>Record-keeping and compliance</li>
          </ul>
          <p>
            You will be notified at the start of any call that may be recorded. If you do not wish 
            to be recorded, please inform our team.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not responsible for the 
            privacy practices of these external sites. We encourage you to read the privacy policies 
            of any linked websites you visit.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. Any changes will be posted on this 
            page with an updated revision date. We encourage you to review this policy periodically.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or wish to exercise your rights, 
            please contact us:
          </p>
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={18} />
              <span>hello@custodianai.co.nz</span>
            </div>
            <div className="contact-item">
              <Phone size={18} />
              <span>+64 210 851 048</span>
            </div>
            <div className="contact-item">
              <MapPin size={18} />
              <span>Auckland, New Zealand</span>
            </div>
          </div>
        </section>

        <section className="legal-section">
          <h2>12. Privacy Commissioner</h2>
          <p>
            If you are not satisfied with our response to your privacy concerns, you have the right 
            to lodge a complaint with the Office of the Privacy Commissioner:
          </p>
          <div className="contact-info">
            <p><strong>Office of the Privacy Commissioner</strong></p>
            <p>PO Box 10094, Wellington 6143</p>
            <p>Phone: 0800 803 909</p>
            <p>Website: <a href="https://www.privacy.org.nz" target="_blank" rel="noopener noreferrer">www.privacy.org.nz</a></p>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
